// src/components/features/journey-planning/DetailsPage/JourneyPlanDetailsPage.tsx
import { StateMessage } from '../../../base/StateMessage/StateMessage';
import { DetailsPage, PagePadding } from '../../../widget/Pages/Pages';
import { Tab, Tabs } from '../../../base/Tabs/Tabs';
import { StopDeparturesDetailsPage } from '../../departures/StopDetailsPage/StopDeparturesDetailsPage';
import { LineTimetableDetailsPage } from '../../timetables/DetailsPage/LineDetailsPage';
import { JourneyPlanItinerary } from '../Itinerary/JourneyPlanItinerary';
import { JourneyPlanMap } from '../JourneyPlanMap/JourneyPlanMap';
import type { JourneyPlan } from '../../../../arcticapi/types';
import type { JourneyPlanSearchRequest } from '../../../../data/journeyPlanSearch';
import { createTimetableDetailsLine } from '../../../../data/timetableDetailsLine';
import type { ItineraryBoardingStep } from '../itinerarySteps';
import type {
  JourneyPlannerDetailsPage,
  PlanLineTimetableDetailsPageOptions,
  PlanStopDeparturesDetailsPageOptions,
  TimetableStopDeparturesDetailsPageOptions,
} from '../../../widget/pageState';
// styles
import styles from './JourneyPlanDetailsPage.module.css';

interface JourneyPlanDetailsPageProps {
  plan: JourneyPlan | null;
  detailsPage: JourneyPlannerDetailsPage | null;
  locations?: Pick<JourneyPlanSearchRequest, 'destination' | 'origin'> | null;
  activeDetailsTab: 'itinerary' | 'map';
  onTabChange: (nextTabId: 'itinerary' | 'map') => void;
  onShowStopDeparturesDetails?: (
    options: PlanStopDeparturesDetailsPageOptions,
  ) => void;
  onShowLineTimetableDetails?: (
    options: PlanLineTimetableDetailsPageOptions,
  ) => void;
}

/**
 * Journey planning details with itinerary and map tabs.
 */
export function JourneyPlanDetailsPage({
  plan,
  detailsPage,
  locations,
  activeDetailsTab,
  onTabChange,
  onShowStopDeparturesDetails,
  onShowLineTimetableDetails,
}: JourneyPlanDetailsPageProps) {
  const handleShowStopDeparturesDetails = (step: ItineraryBoardingStep) => {
    if (detailsPage?.type !== 'itinerary' || !step.stopAtcoCode) {
      return;
    }

    onShowStopDeparturesDetails?.({
      itineraryId: detailsPage.itineraryId,
      stopAtcoCode: step.stopAtcoCode,
      stopName: step.stopName ?? null,
    });
  };

  const handleShowLineTimetableDetails = (step: ItineraryBoardingStep) => {
    const lineId = step.lineHref ?? step.timetableHref;

    if (detailsPage?.type !== 'itinerary' || !lineId) {
      return;
    }

    onShowLineTimetableDetails?.({
      itineraryId: detailsPage.itineraryId,
      lineId,
      initialLine: createTimetableDetailsLine({
        id: lineId,
        name: step.lineName ?? step.service,
        title: step.lineName ?? step.service,
        description: step.lineName ?? step.service,
        colors: step.lineColor
          ? {
              background: step.lineColor,
            }
          : undefined,
        timetableHref: step.timetableHref,
      }),
    });
  };

  const handleShowTimetableStopDeparturesDetails = (
    options: TimetableStopDeparturesDetailsPageOptions,
  ) => {
    if (detailsPage?.type !== 'line-timetable') {
      return;
    }

    onShowStopDeparturesDetails?.({
      itineraryId: detailsPage.itineraryId,
      stopAtcoCode: options.stopAtcoCode,
      stopName: options.stopName ?? null,
    });
  };

  if (!plan) {
    return (
      <DetailsPage>
        <StateMessage
          state="error"
          title="Journey details unavailable"
          message="We couldn't load this journey. Go back to results and try another option."
        />
      </DetailsPage>
    );
  }

  if (detailsPage?.type === 'stop-departures') {
    return (
      <StopDeparturesDetailsPage
        selectedStopAtcoCode={detailsPage.stopAtcoCode}
        fallbackStopName={detailsPage.stopName ?? null}
      />
    );
  }

  if (detailsPage?.type === 'line-timetable') {
    return (
      <LineTimetableDetailsPage
        selectedLineId={detailsPage.lineId}
        initialLine={detailsPage.initialLine ?? null}
        onShowStopDeparturesDetails={handleShowTimetableStopDeparturesDetails}
      />
    );
  }

  return (
    <DetailsPage>
      <Tabs
        activeTab={activeDetailsTab}
        onTabChange={(nextTabId) => {
          if (nextTabId === 'itinerary' || nextTabId === 'map') {
            onTabChange(nextTabId);
          }
        }}
        classNames={{
          root: styles.detailsTabs,
          tabBar: styles.detailsTabBar,
          tab: styles.detailsTab,
          activeTab: styles.detailsTabActive,
          content: styles.detailsTabContent,
          panel: styles.detailsTabPanel,
        }}
      >
        <Tab id="itinerary" label="Planner">
          <PagePadding>
            <JourneyPlanItinerary
              plan={plan}
              aria-label="Full itinerary"
              onShowStopDeparturesDetails={handleShowStopDeparturesDetails}
              onShowLineTimetableDetails={handleShowLineTimetableDetails}
            />
          </PagePadding>
        </Tab>
        <Tab id="map" label="Map">
          <JourneyPlanMap
            plan={plan}
            locations={locations}
            aria-label="Journey map"
          />
        </Tab>
      </Tabs>
    </DetailsPage>
  );
}
